<template>
    <div class="ProjectJoin compact" :class="[{'joined': isUserJoined}]">

        <p v-if="!compact" class="ProjectJoin-description">{{ $t('projects.joinDesc') }}</p>
        
        <!-- User's access status -->
        <div v-if="isUserLoggedIn && isUserJoined" class="ProjectJoin-status success">
            <!-- User has joined -->
            <template v-if="isUserJoined">
                <CloIcon symbol="check" />
                <p v-if="isUserJoined">{{ $t('projects.joinSuccess') }}</p>
            </template>
        </div>

        <!-- Tip for those who have joined -->
		<template v-if="isUserJoined">
			<p class="u-text-2 u-margin-none">{{ $t('projects.joinTip') }}</p>
		</template>

        <!-- Join action -->
        <div v-if="accessAffordance" class="ProjectJoin-action">
            
            <!-- Button -->
            <a v-if="accessAffordance=='login'" class="Button Button--small" :href="logInUrl">{{ $t('projects.loginJoinButton') }}</a>
            <CloButton v-if="accessAffordance=='join'" size="md" color="secondary" @click.native="joinProject">{{ $t('projects.joinButton') }}</CloButton>
            <CloButton v-if="accessAffordance=='leave'" size="sm" @click.native="leaveProject">{{ $t('projects.leaveButton') }}</CloButton>
            
            <!-- Status messages -->
            <div class="ProjectJoin-action-status" :class="[{'success' : joinStatus.success | leaveStatus.success }, {'error' : joinStatus.error | leaveStatus.error }]">
            
                <!-- Join statuses -->
                <template v-if="joinStatus.inProgress">
                    <CloIcon symbol="spinner" />
                    <span>{{ $t('projects.joinStatus.inProgress') }}</span>
                </template>
                <template v-if="joinStatus.success">
                    <CloIcon symbol="check" />
                    <span>{{ $t('projects.joinStatus.success') }}</span>
                </template>
                <template v-if="joinStatus.error">
                    <CloIcon symbol="warning" />
                    <span>{{ $t('projects.joinStatus.error') }}</span>
                </template>
            
                <!-- Leave statuses -->
                <template v-if="leaveStatus.inProgress">
                    <CloIcon symbol="spinner" />
                    <span>{{ $t('projects.leaveStatus.inProgress') }}</span>
                </template>
                <template v-if="leaveStatus.success">
                    <CloIcon symbol="check" />
                    <span>{{ $t('projects.leaveStatus.success') }}</span>
                </template>
                <template v-if="leaveStatus.error">
                    <CloIcon symbol="warning" />
                    <span>{{ $t('projects.leaveStatus.error') }}</span>
                </template>
            </div>
        </div>
    </div>
</template>


<script lang="ts">
import Vue from 'vue'
import axios from 'axios'

export default Vue.extend({
    data() {
        return {
            joinStatus: {
                inProgress: false,
                success: false,
                error: false,
            },
            leaveStatus: {
                inProgress: false,
                success: false,
                error: false,
            }
        }
    },
    props: {
        csrfToken: {
            type: String,
            required: true,
        },
        baseUrl: {
            type: String,
            required: false,
        },
        logInUrl: {
            type: String,
            required: false,
        },
        projectId: {
            type: String,
            required: false,
        },
        userIsLoggedIn: {
            type: String,
            required: true,
        },
        userIsJoined: {
            type: String,
            required: false,
        },
    },
    computed: {
        accessAffordance() {
            // accessAffordance describes what the user can do with regard to project access
            // The four states are: login, join, leave, NULL
            // If user is logged in
            if (this.isUserLoggedIn){
                // If user has joined project
                if (this.isUserJoined){
                    // User can leave project
                    return 'leave'
                }
                else {
                    // User can join project
                    return 'join'
                }
            } 
            // If user is NOT logged in
            else {
                // User can log-in to join project
                return 'login'
            }
        },
        isUserJoined() {
            return this.userIsJoined === 'true'
        },
        isUserLoggedIn() {
            return this.userIsLoggedIn === 'true'
        }
    },
    methods: {
        joinProject() {
            if (this.isUserLoggedIn) {
                console.log(`Joining project ${this.projectId}...`);
                // Set inProgress state
                this.joinStatus.inProgress = true;
                this.joinStatus.success = false;
                this.joinStatus.error = false;
                // TODO: UPDATE FOR EBIRD WEB
                const url = `${this.baseUrl}/project/${this.projectId}/membership`;
                axios({
                    method: 'post',
                    url,
                    headers: {
                        'X-CSRF-TOKEN': this.csrfToken,
                    },
                })
                .then((res) => {
                    // Set success state
                    this.joinStatus.inProgress = false;
                    this.joinStatus.success = true;
                    this.joinStatus.error = false;
                    console.log(`SUCCESS joining project ${this.projectId}`);
                    // reload page to reflect new status
                    window.location.reload();
                })
                .catch((err) => {
                    // Set error state
                    this.joinStatus.inProgress = false;
                    this.joinStatus.success = false;
                    this.joinStatus.error = true;
                    console.log(`FAILURE joining project ${this.projectId}`);
                });
            }
        },
        leaveProject() {
            if (this.isUserLoggedIn) {
                console.log(`Leaving project ${this.projectId}...`);
                // Set inProgress state
                this.leaveStatus.inProgress = true;
                this.leaveStatus.success = false;
                this.leaveStatus.error = false;
                const url = `${this.baseUrl}/project/${this.projectId}/membership`;
                axios({
                    method: 'delete',
                    url,
                    headers: {
                        'X-CSRF-TOKEN': this.csrfToken,
                    },
                })
                .then((res) => {
                    // Set success state
                    this.leaveStatus.inProgress = false;
                    this.leaveStatus.success = true;
                    this.leaveStatus.error = false;
                    console.log(`SUCCESS leaving project ${this.projectId}`);
                    // reload page to reflect new status
                    window.location.reload();
                })
                .catch((err) => {
                    // Set error state
                    this.leaveStatus.inProgress = false;
                    this.leaveStatus.success = false;
                    this.leaveStatus.error = true;
                    console.log(`FAILURE leaving project ${this.projectId}`);
                });
            }
        }
    },
})
</script>

<style lang="scss">
.ProjectJoin {
    background: $brand-color-sky-blue-2;
    border-radius: $space-sm;
    @include inset-md;
    display: flex;
    flex-direction: column;
    gap: $space-sm;
    &--compact {
        @include inset-squish-md;
    }
    &.joined {
        background: $brand-color-forest-green-2;
    }
    &.denied {
        background: $brand-color-sand-2;
    }
}

.ProjectJoin-description {
    // flex-basis: 50%;
    @include text(3);
    margin: 0;
}

.ProjectJoin-status {
    display: flex;
    align-items: center;
    column-gap: $space-sm;
    .Icon {
        fill: $color-text;
    }
    p {
        @include text(3);
        margin: 0;
    }
    &.success {
        color: $color-link-special;
        .Icon {
            fill: $color-link-special;
        }
    }
    &.error {
        color: $color-text-alert;
        .Icon {
            fill: $color-text-alert;
        }
    }
}

.ProjectJoin-action {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    column-gap: $space-md;
    row-gap: $space-sm;
    p, .Button {
        margin: 0;
    }
    .ProjectJoin-action-status {
        @include text(3);
        display: flex;
        align-items: center;
        column-gap: $space-sm;
        .Icon {
            fill: $color-link;
            animation: spin 1.2s linear infinite;
        }
        &.success {
            color: $color-link-special;
            .Icon {
                fill: $color-link-special;
                animation: none;
            }
        }
        &.error {
            color: $color-text-alert;
            .Icon {
                fill: $color-text-alert;
                animation: none;
            }
        }
    }
}
</style>